"use client"

import { useEffect, useState } from "react"
import { Label } from "@/components/Label/Label.component"
import { useChronology } from "@/contexts/chronology/chronology.context"
import { useSite } from "@/contexts/site/site.context"
import type { ZonedDateTime } from "@/ts/date-time/date-time.types"
import { PublicationDateTimeLoader } from "../PublicationDateTimeLoader/PublicationDateTimeLoader.component"

type RelativePublicationDateTimeProps = {
  readonly className?: string
  readonly zonedDateTime: ZonedDateTime
  readonly size?: "xs" | "sm"
}

export function RelativePublicationDateTime({
  className,
  zonedDateTime,
  size = "sm",
}: RelativePublicationDateTimeProps) {
  const { locale } = useSite()
  const {
    isToday,
    toShortDateWithoutYearString,
    toShortTimeString,
    isWithinLastHour,
    toRelativeDateTimeString,
  } = useChronology()
  const [formattedDateTime, setFormattedDateTime] = useState<null | string>(
    null,
  )

  useEffect(() => {
    function getTimeFormat(date: Date) {
      if (isWithinLastHour(date)) {
        return toRelativeDateTimeString(date)
      }

      if (isToday(date)) {
        const formattedTime = toShortTimeString(date)

        return {
          da: `kl. ${formattedTime}`,
          de: `um ${formattedTime} Uhr`,
          en: `at ${formattedTime}`,
          no: `kl. ${formattedTime}`,
          sv: `kl. ${formattedTime}`,
        }[locale]
      }

      return toShortDateWithoutYearString(date)
    }

    const dateToFormat = new Date(zonedDateTime)
    const dateTime = getTimeFormat(dateToFormat)
    setFormattedDateTime(dateTime)
  }, [
    isToday,
    toShortDateWithoutYearString,
    toShortTimeString,
    zonedDateTime,
    isWithinLastHour,
    toRelativeDateTimeString,
    locale,
  ])

  return (
    <Label size={size}>
      {formattedDateTime !== null ? (
        <time className={className} dateTime={zonedDateTime}>
          {formattedDateTime}
        </time>
      ) : (
        <PublicationDateTimeLoader className="w-9" />
      )}
    </Label>
  )
}
